import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtDate, fmtErr } from "../../api";
import Layout from "../../components/Layout";
import { useAuth } from "../../AuthContext";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Owners() {
  const { user } = useAuth();
  const isOperator = user?.role === "operator";
  const [owners, setOwners] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [invite, setInvite] = useState({ name: "", email: "", phone: "" });
  const [inviteProps, setInviteProps] = useState([]);
  const [pwFor, setPwFor] = useState(null);
  const [tempPw, setTempPw] = useState("");
  const [editFor, setEditFor] = useState(null);
  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [q, setQ] = useState("");

  const load = () => api.get("/owners").then((r) => setOwners(r.data)).catch(() => toast.error("Could not load owners"));
  const deleteOwner = async (o) => {
    if (!window.confirm(`Delete owner ${o.name} (${o.email}) permanently? Their login is removed. This can't be undone.`)) return;
    try { await api.delete(`/owners/${o.id}`); toast.success("Owner removed"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only load by design
  useEffect(() => { load(); }, []);

  const toggleActive = async (o) => {
    try {
      await api.patch(`/owners/${o.id}`, { active: o.active === false });
      toast.success(o.active === false ? "Owner reactivated — their properties are active again" : "Owner deactivated — their properties are now inactive");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const sendReset = async (o) => {
    try {
      const r = await api.post(`/owners/${o.id}/reset-password`, {});
      if (r.data.email_sent === false) {
        toast.error(`Couldn't send to ${o.email} — check the address, or use Set password instead`);
      } else {
        toast.success(r.data.method === "email_invite" ? `Invite re-sent to ${o.email} — link good for 3 days` : `Reset link emailed to ${o.email} (expires in 1 hour)`);
      }
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const setPassword = async (o) => {
    try {
      await api.post(`/owners/${o.id}/reset-password`, { password: tempPw });
      toast.success("Password updated — share it with the owner");
      setPwFor(null); setTempPw("");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const saveEdit = async (o) => {
    try {
      await api.patch(`/owners/${o.id}`, { name: editName.trim() || o.name, phone: editPhone.trim() });
      toast.success("Owner updated");
      setEditFor(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/owners/invite", { ...invite, properties: inviteProps.filter((p) => p.name && p.address).map(({ _k, ...p }) => p) });
      if (r.data.email_sent === false) toast.error("Owner created, but the invite email could not be sent — check the address or use Set password");
      else toast.success(`Invite emailed — ${r.data.properties_created} propert${r.data.properties_created === 1 ? "y" : "ies"} linked`);
      setShowInvite(false); setInvite({ name: "", email: "", phone: "" }); setInviteProps([]);
      load();
    } catch (e2) { toast.error(fmtErr(e2)); }
  };

  return (
    <Layout title="Owners">
      {isOperator && (
        <button data-testid="invite-owner-btn" onClick={() => setShowInvite(!showInvite)}
          className="mb-4 flex h-14 w-full items-center justify-center gap-2 rounded-md bg-primary font-bold text-primary-foreground">
          <Plus size={20} weight="bold" /> Invite an owner
        </button>
      )}
      {showInvite && (
        <form onSubmit={sendInvite} className="card-flat mb-6 space-y-3 p-4" data-testid="invite-owner-form">
          <p className="text-sm text-muted-foreground">Create the owner's account (and their properties, if you know them). They get an email link to set their own password — the link stays valid for 3 days.</p>
          <input data-testid="invite-name" className="field-input w-full" placeholder="Owner name" value={invite.name}
            onChange={(e) => setInvite({ ...invite, name: e.target.value })} required />
          <input data-testid="invite-email" type="email" className="field-input w-full" placeholder="Owner email" value={invite.email}
            onChange={(e) => setInvite({ ...invite, email: e.target.value })} required />
          <input data-testid="invite-phone" type="tel" className="field-input w-full" placeholder="Owner mobile (optional — for calls/text alerts)" value={invite.phone}
            onChange={(e) => setInvite({ ...invite, phone: e.target.value })} />
          {inviteProps.map((p, i) => (
            <div key={p._k} className="space-y-2 rounded-md border border-border p-3" data-testid={`invite-prop-${i}`}>
              <div className="flex gap-2">
                <input data-testid={`invite-prop-name-${i}`} className="field-input flex-1" placeholder="Property name" value={p.name}
                  onChange={(e) => setInviteProps(inviteProps.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
                <button type="button" data-testid={`invite-prop-del-${i}`} onClick={() => setInviteProps(inviteProps.filter((_, j) => j !== i))}
                  className="h-12 w-12 rounded-md border border-border text-destructive"><Trash size={18} className="mx-auto" /></button>
              </div>
              <input data-testid={`invite-prop-address-${i}`} className="field-input w-full" placeholder="Address" value={p.address}
                onChange={(e) => setInviteProps(inviteProps.map((x, j) => (j === i ? { ...x, address: e.target.value } : x)))} />
            </div>
          ))}
          <button type="button" data-testid="invite-add-prop" onClick={() => setInviteProps([...inviteProps, { name: "", address: "", _k: crypto.randomUUID() }])}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-md border border-dashed border-border text-sm font-bold text-muted-foreground">
            <Plus size={16} weight="bold" /> Add a property for them
          </button>
          <button data-testid="invite-send" className="btn-primary-big !h-14 !text-base">Send invite email</button>
        </form>
      )}
      <input data-testid="search-input" className="field-input mb-4 w-full" placeholder="Search owner name or email…" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="space-y-3" data-testid="owners-list">
        {!owners && <p className="text-muted-foreground">Loading…</p>}
        {owners?.length === 0 && <p className="card-flat p-6 text-sm text-muted-foreground">No owners yet — invite one above, or share your invite code from Settings.</p>}
        {owners?.filter((o) => !q || [o.name, o.email, o.phone].join(" ").toLowerCase().includes(q.toLowerCase())).map((o) => (
          <div key={o.id} className="card-flat space-y-3 p-4" data-testid={`owner-card-${o.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <Link to={`/owners/${o.id}`} data-testid={`owner-open-${o.id}`} className="font-semibold text-primary">{o.name}</Link>
                <p className="text-sm text-muted-foreground">{o.email}</p>
                {o.phone
                  ? <a data-testid={`owner-phone-${o.id}`} href={`tel:${o.phone}`} className="block text-sm font-semibold text-primary">{o.phone}</a>
                  : <p className="text-xs text-muted-foreground" data-testid={`owner-phone-missing-${o.id}`}>No phone on file</p>}
                {o.sms_opted_out && <span data-testid={`owner-opted-out-${o.id}`} className="mt-1 inline-block rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-bold text-destructive">Opted out of texts</span>}
                {o.contact_only && <span data-testid={`owner-contact-only-${o.id}`} className="mt-1 inline-block rounded-full bg-muted px-2 py-0.5 text-xs font-bold text-muted-foreground">Contact only — no portal access</span>}
                <p className="text-xs text-muted-foreground">{o.property_count} propert{o.property_count === 1 ? "y" : "ies"}</p>
                {o.last_login_at
                  ? <p className="text-xs text-muted-foreground" data-testid={`owner-seen-${o.id}`}>Last seen {fmtDate(o.last_login_at)}</p>
                  : <p className="text-xs font-bold text-[hsl(24,90%,42%)]" data-testid={`owner-invited-${o.id}`}>Invited {fmtDate(o.created_at)} — hasn't joined yet</p>}
              </div>
              <span data-testid={`owner-status-${o.id}`} className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${o.active === false ? "bg-destructive/10 text-destructive" : "bg-primary/10 text-primary"}`}>
                {o.active === false ? "Inactive" : "Active"}
              </span>
            </div>
            {isOperator && (
              <div className="flex flex-wrap gap-2">
                <button data-testid={`owner-reset-email-${o.id}`} onClick={() => sendReset(o)}
                  className="h-10 rounded-md border border-border px-3 text-xs font-bold">{o.last_login_at ? "Email reset link" : o.contact_only ? "Invite to portal" : "Resend invite"}</button>
                <button data-testid={`owner-set-pw-${o.id}`} onClick={() => { setPwFor(pwFor === o.id ? null : o.id); setTempPw(""); }}
                  className="h-10 rounded-md border border-border px-3 text-xs font-bold">Set password</button>
                <button data-testid={`owner-edit-${o.id}`} onClick={() => { setEditFor(editFor === o.id ? null : o.id); setEditName(o.name || ""); setEditPhone(o.phone || ""); }}
                  className="h-10 rounded-md border border-border px-3 text-xs font-bold">Edit</button>
                <a data-testid={`owner-send-quote-${o.id}`} href={`/quotes/new?owner=${o.id}`}
                  className="flex h-10 items-center rounded-md border border-primary px-3 text-xs font-bold text-primary">Send quote</a>
                <button data-testid={`owner-toggle-${o.id}`} onClick={() => toggleActive(o)}
                  className={`h-10 rounded-md border px-3 text-xs font-bold ${o.active === false ? "border-primary text-primary" : "border-destructive text-destructive"}`}>
                  {o.active === false ? "Reactivate" : "Deactivate"}
                </button>
                {o.property_count === 0 && (
                  <button data-testid={`owner-delete-${o.id}`} onClick={() => deleteOwner(o)}
                    className="h-10 rounded-md border border-destructive px-3 text-xs font-bold text-destructive">Delete</button>
                )}
              </div>
            )}
            {isOperator && pwFor === o.id && (
              <div className="flex gap-2" data-testid={`owner-pw-form-${o.id}`}>
                <input data-testid={`owner-pw-input-${o.id}`} type="text" className="field-input flex-1" placeholder="New temporary password (8+ chars)"
                  value={tempPw} onChange={(e) => setTempPw(e.target.value)} />
                <button data-testid={`owner-pw-save-${o.id}`} onClick={() => setPassword(o)}
                  className="h-12 rounded-md bg-primary px-4 text-sm font-bold text-primary-foreground">Save</button>
              </div>
            )}
            {isOperator && editFor === o.id && (
              <div className="space-y-2" data-testid={`owner-edit-form-${o.id}`}>
                <input data-testid={`owner-name-input-${o.id}`} className="field-input w-full" placeholder="Owner name"
                  value={editName} onChange={(e) => setEditName(e.target.value)} />
                <div className="flex gap-2">
                  <input data-testid={`owner-phone-input-${o.id}`} type="tel" className="field-input flex-1" placeholder="Mobile number (e.g. 705-555-1234)"
                    value={editPhone} onChange={(e) => setEditPhone(e.target.value)} />
                  <button data-testid={`owner-edit-save-${o.id}`} onClick={() => saveEdit(o)}
                    className="h-12 rounded-md bg-primary px-4 text-sm font-bold text-primary-foreground">Save</button>
                </div>
              </div>
            )}
            {o.active === false && (
              <p className="rounded-md bg-muted p-3 text-xs text-muted-foreground">
                Inactive owners can't sign in and their properties are inactive (no new visits generate). Reactivating restores both.
              </p>
            )}
          </div>
        ))}
      </div>
    </Layout>
  );
}
